import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogActions, Box, Tabs, Tab, Typography,
  Avatar, Chip, Stack, Grid, Paper, Divider,
  Button, IconButton, CircularProgress, Alert,
} from '@mui/material';
import {
  Close as CloseIcon, Person as PersonIcon, School as SchoolIcon,
  Group as ParentIcon, HealthAndSafety as HealthIcon,
  EventBusy as LeaverIcon, FamilyRestroom as FamilyIcon,
  StickyNote2 as NotesIcon,
} from '@mui/icons-material';
import API from '../../BaseURL';

function Row({ label, value }) {
  return (
    <Stack direction="row" spacing={1} sx={{ py: 0.6 }}>
      <Typography variant="caption" color="text.secondary"
        sx={{ minWidth: 140, fontWeight: 700, textTransform: 'uppercase',
          letterSpacing: 0.5, fontSize: 10 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ flex: 1 }}>{value || '—'}</Typography>
    </Stack>
  );
}

function fmtDate(v) {
  if (!v) return '';
  // Most child_info dates come through as YYYY-MM-DD strings already.
  // If we get a Date / ISO timestamp, format it nicely.
  try {
    if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v)) {
      const d = new Date(v);
      if (!isNaN(d)) return d.toLocaleDateString('en-GH', { year: 'numeric', month: 'short', day: 'numeric' });
    }
    return String(v);
  } catch { return String(v); }
}

export default function StudentDetailsDialog({ open, onClose, source = 'student', transCode }) {
  const [tab, setTab]   = useState(0);
  const [data, setData] = useState(null);
  const [loading, setL] = useState(true);
  const [error, setErr] = useState('');

  useEffect(() => {
    if (!open || !transCode) return;
    setTab(0); setL(true); setErr(''); setData(null);
    const url = source === 'leaver'
      ? `/admissions/leavers/${transCode}/details`
      : `/admissions/students/${transCode}/details`;
    API.get(url)
      .then(r => setData(r.data))
      .catch(e => setErr(e.response?.data?.message || 'Failed to load'))
      .finally(() => setL(false));
  }, [open, transCode, source]);

  const s = data?.student;
  const parents = data?.parents || { father: {}, mother: {} };
  const isLeaver = s?._source === 'leaver';
  const isAlumni = s?.calss === 'Alumna';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      {/* ── Header strip ───────────────────────────────────────── */}
      <Box sx={{
        background: (t) => `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.secondary.main} 100%)`,
        color: '#fff', px: 3, py: 2.5,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
      }}>
        <Stack direction="row" spacing={2} alignItems="center">
          {s?.image ? (
            <Avatar src={s.image} sx={{ width: 64, height: 64,
              border: '3px solid rgba(255,255,255,0.3)' }} />
          ) : (
            <Avatar sx={{ width: 64, height: 64, bgcolor: 'rgba(255,255,255,0.2)',
              fontSize: 24, fontWeight: 800 }}>
              {(s?.firstname?.[0] || '?').toUpperCase()}
            </Avatar>
          )}
          <Box>
            <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.1 }}>
              {s ? `${s.firstname} ${s.lastname}` : 'Student Details'}
            </Typography>
            <Stack direction="row" spacing={0.8} mt={0.6} flexWrap="wrap" rowGap={0.5}>
              {s?.index_no && (
                <Chip size="small" label={s.index_no}
                  sx={{ fontFamily: 'monospace', bgcolor: 'rgba(255,255,255,0.2)',
                    color: '#fff', fontWeight: 600 }} />
              )}
              {s?.calss && (
                <Chip size="small" label={s.calss === 'Alumna' ? 'Alumni' : s.calss}
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 600 }} />
              )}
              {s?.gender && (
                <Chip size="small" label={s.gender}
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 600 }} />
              )}
              {s?.type && (
                <Chip size="small" label={s.type}
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 600 }} />
              )}
              {isLeaver && (
                <Chip size="small" label="LEAVER"
                  sx={{ bgcolor: '#ed6c02', color: '#fff', fontWeight: 700 }} />
              )}
              {isAlumni && (
                <Chip size="small" label="ALUMNI"
                  sx={{ bgcolor: '#2e7d32', color: '#fff', fontWeight: 700 }} />
              )}
            </Stack>
          </Box>
        </Stack>
        <IconButton onClick={onClose} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
      </Box>

      {/* ── Tabs ───────────────────────────────────────────────── */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.default' }}>
        <Tab label="Profile"  icon={<PersonIcon  fontSize="small" />} iconPosition="start" />
        <Tab label="Academic" icon={<SchoolIcon  fontSize="small" />} iconPosition="start" />
        <Tab label="Parents"  icon={<FamilyIcon  fontSize="small" />} iconPosition="start" />
        <Tab label="Guardian" icon={<ParentIcon  fontSize="small" />} iconPosition="start" />
        <Tab label="Health"   icon={<HealthIcon  fontSize="small" />} iconPosition="start" />
        <Tab label="Notes"    icon={<NotesIcon   fontSize="small" />} iconPosition="start" />
        {isLeaver && (
          <Tab label="Leaver Info" icon={<LeaverIcon fontSize="small" />} iconPosition="start" />
        )}
      </Tabs>

      <DialogContent dividers sx={{ bgcolor: 'background.default', minHeight: 360 }}>
        {loading && <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>}
        {error   && <Alert severity="error">{error}</Alert>}

        {!loading && s && (
          <>
            {/* ── Profile ─────────────────────────────────────── */}
            {tab === 0 && (
              <Paper sx={{ p: 2.5 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Row label="Index Number"  value={s.index_no} />
                    <Row label="First Name"    value={s.firstname} />
                    <Row label="Last Name"     value={s.lastname} />
                    <Row label="Gender"        value={s.gender} />
                    <Row label="Date of Birth" value={fmtDate(s.dob)} />
                    <Row label="Religion"      value={s.rele} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Row label="Registration Date" value={fmtDate(s.re_dat)} />
                    <Row label="Status" value={
                      <Chip size="small"
                        label={isLeaver ? 'Leaver' : isAlumni ? 'Alumni' : 'Active'}
                        color={isLeaver ? 'warning' : isAlumni ? 'success' : 'primary'}
                        variant="outlined" />
                    } />
                    <Row label="Mobile (own)"  value={s.mobi} />
                    <Row label="Residence"     value={s.p_res} />
                  </Grid>
                </Grid>
              </Paper>
            )}

            {/* ── Academic ────────────────────────────────────── */}
            {tab === 1 && (
              <Paper sx={{ p: 2.5 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Row label="Current Class"     value={s.calss === 'Alumna' ? 'Alumni' : s.calss} />
                    <Row label="Class on Admission" value={s.cur_class} />
                    <Row label="Student Type"      value={s.type} />
                    <Row label="Boarding House"    value={s.house} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Row label="Stay-In"            value={
                      Number(s.stay) === 1 ? 'Yes' : Number(s.stay) === 0 ? 'No' : '—'
                    } />
                    <Row label="Previous School"    value={s.presch} />
                    <Row label="Registration Date"  value={fmtDate(s.re_dat)} />
                  </Grid>
                </Grid>
              </Paper>
            )}

            {/* ── Parents ─────────────────────────────────────── */}
            {tab === 2 && (
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Paper sx={{ p: 2.5, flex: 1 }}>
                  <Typography variant="overline" color="primary" fontWeight={700}>Father</Typography>
                  <Divider sx={{ my: 1 }} />
                  <Row label="Full Name"   value={parents.father?.full_n} />
                  <Row label="Mobile"      value={parents.father?.mo_num} />
                  <Row label="Email"       value={parents.father?.e_add} />
                  <Row label="Home Phone"  value={parents.father?.ho_tele_num} />
                  <Row label="Work Phone"  value={parents.father?.wo_tel_num} />
                  <Row label="Occupation"  value={parents.father?.occu} />
                  <Row label="Employer"    value={parents.father?.emp_na} />
                  <Row label="Employer Address" value={parents.father?.emp_add} />
                  <Row label="Education"   value={parents.father?.highest} />
                  <Row label="Address"     value={parents.father?.mail_add} />
                </Paper>
                <Paper sx={{ p: 2.5, flex: 1 }}>
                  <Typography variant="overline" color="secondary" fontWeight={700}>Mother</Typography>
                  <Divider sx={{ my: 1 }} />
                  <Row label="Full Name"   value={parents.mother?.full_n} />
                  <Row label="Mobile"      value={parents.mother?.mo_num} />
                  <Row label="Email"       value={parents.mother?.e_add} />
                  <Row label="Home Phone"  value={parents.mother?.ho_tele_num} />
                  <Row label="Work Phone"  value={parents.mother?.wo_tel_num} />
                  <Row label="Occupation"  value={parents.mother?.occu} />
                  <Row label="Employer"    value={parents.mother?.emp_na} />
                  <Row label="Employer Address" value={parents.mother?.emp_add} />
                  <Row label="Education"   value={parents.mother?.highest} />
                  <Row label="Address"     value={parents.mother?.mail_add} />
                </Paper>
              </Stack>
            )}

            {/* ── Guardian ────────────────────────────────────── */}
            {tab === 3 && (
              <Paper sx={{ p: 2.5 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Row label="Full Name"     value={s.p_name} />
                    <Row label="Relationship"  value={s.p_rele} />
                    <Row label="Position"      value={s.p_pos} />
                    <Row label="Occupation"    value={s.p_occ} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Row label="Mobile"        value={s.mobi || s.p_t_m} />
                    <Row label="Home Phone"    value={s.p_t_h} />
                    <Row label="Office Phone"  value={s.p_t_of} />
                    <Row label="Email"         value={s.p_email} />
                    <Row label="Residence"     value={s.p_res} />
                  </Grid>
                </Grid>
              </Paper>
            )}

            {/* ── Health & Emergency ─────────────────────────── */}
            {tab === 4 && (
              <Paper sx={{ p: 2.5 }}>
                <Typography variant="overline" color="primary" fontWeight={700}>Health</Typography>
                <Divider sx={{ my: 1 }} />
                <Row label="Any Allergies" value={s.alle} />
                {s.alle === 'Yes' && (
                  <>
                    <Row label="Health Conditions" value={s.he_con} />
                    <Row label="Medical History"   value={s.me_hi} />
                  </>
                )}
                <Box sx={{ mt: 2 }}>
                  <Typography variant="overline" color="error" fontWeight={700}>Emergency Contact</Typography>
                  <Divider sx={{ my: 1 }} />
                  <Row label="Contact Name"  value={s.e_na} />
                  <Row label="Contact Phone" value={s.tele} />
                </Box>
              </Paper>
            )}

            {/* ── Notes ──────────────────────────────────────── */}
            {tab === 5 && (
              <Paper sx={{ p: 2.5 }}>
                <Typography variant="overline" color="primary" fontWeight={700}>Admin Notes</Typography>
                <Divider sx={{ my: 1 }} />
                <Row label="Note"       value={s.note} />
                <Row label="Other Info" value={s.other_info} />
                {!s.note && !s.other_info && (
                  <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic', mt: 1 }}>
                    No admin notes recorded for this student.
                  </Typography>
                )}
              </Paper>
            )}

            {/* ── Leaver info (only when source = leaver) ────── */}
            {tab === 6 && isLeaver && (
              <Paper sx={{ p: 2.5 }}>
                <Alert severity="warning" sx={{ mb: 2 }} icon={<LeaverIcon />}>
                  This student is on the leavers register.
                </Alert>
                <Row label="Date Left"
                  value={s.left_at ? new Date(s.left_at).toLocaleString('en-GH') : '—'} />
                <Row label="Reason"     value={s.left_reason} />
              </Paper>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: 'background.default' }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
